import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { TrailerDetailsForm } from '../components/TrailerDetailsForm';
import { TrailerDocuments } from '../components/TrailerDocuments';
import { TrailerHistoryTab } from '../components/TrailerHistoryTab';
import { TrailerOwnershipTab } from '../components/TrailerOwnershipTab';
import { TrailerStatusControl } from '../components/TrailerStatusControl';
import { TRAILER_TAB_LABELS, TRAILER_TABS, trailerTabPath, type TrailerTab } from '../trailers.paths';
import { useTrailerDetail } from '../hooks/use-trailer-detail';
import { withinSection } from '../../../../../shared/components/section-trail';
import { FLEET_TAB_LABELS, fleetTabPath } from '../../../fleet.paths';

const TRAILER_TRAIL = withinSection('fleet', { label: FLEET_TAB_LABELS.trailers, to: fleetTabPath('trailers') });

/**
 * A trailer's own page: fields, documents, ownership and history each on their own tab, same
 * shape as TruckDetailPage. `fleet.view` reads; `truck.manage` mutates the record, `document.manage`
 * mutates its documents (gitaumoses4/chukua#13, #15, #17) — every edit control here is hidden or
 * disabled for a visitor without it, same as CustomerDetailPage. Pairing to trips is out of
 * scope here.
 */
export function TrailerDetailPage({ tab }: { tab: TrailerTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, updateError, saving, update, activate, deactivate, documentSubmitting, addDocument, openDocument } = useTrailerDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('truck.manage');
  const canManageDocuments = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('document.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading trailer" breadcrumbs={TRAILER_TRAIL} tabs={TRAILER_TABS.length} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Trailer not found" description="This trailer doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this trailer" description="Something went wrong loading this trailer. Try reloading the page." />
    );
  }

  const { trailer } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={TRAILER_TRAIL}
        title={trailer.registration}
        description={trailer.typeCode}
        actions={<TrailerStatusControl trailer={trailer} canManage={canManage} onActivate={activate} onDeactivate={deactivate} />}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(trailerTabPath(trailer.id, key as TrailerTab))}
        items={[
          {
            key: 'details',
            label: TRAILER_TAB_LABELS.details,
            children: <TrailerDetailsForm trailer={trailer} canManage={canManage} error={updateError} saving={saving} onSave={update} />,
          },
          {
            key: 'documents',
            label: TRAILER_TAB_LABELS.documents,
            children: (
              <TrailerDocuments
                documents={trailer.documents}
                canManage={canManageDocuments}
                submitting={documentSubmitting}
                onAdd={addDocument}
                onOpen={openDocument}
              />
            ),
          },
          {
            key: 'ownership',
            label: TRAILER_TAB_LABELS.ownership,
            children: <TrailerOwnershipTab trailerId={trailer.id} trailerRegistration={trailer.registration} canManage={canManage} />,
          },
          { key: 'history', label: TRAILER_TAB_LABELS.history, children: <TrailerHistoryTab trailerId={trailer.id} /> },
        ]}
      />
    </>
  );
}
